import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
export function Sidebar({ badges = {}, currentUser = {}, onLogout }) {
  const isAdmin = currentUser?.ruolo === 'admin';
  const isCollab = currentUser?.ruolo === 'collaboratore';
  const moduli = currentUser?.moduli_custom || null;
  const canSee = (key) => {
    if (!isCollab) return true;
    if (!moduli) return false;
    return moduli.includes(key);
  };

  const allItems = [
    { section: 'Principale' },
    { to: '/',          icon: '◈', label: 'Dashboard',          key: 'dashboard' },
    { to: '/clienti',   icon: '◉', label: 'Clienti',            key: 'clienti',  badge: badges.clienti },
    { to: '/scadenze',  icon: '◷', label: 'Scadenze',           key: 'scadenze', badge: badges.scadenze },
    { section: 'Fiscale' },
    { to: '/iva',       icon: '◎', label: 'Dichiarazioni IVA',  key: 'iva' },
    { to: '/redditi',   icon: '◐', label: 'Redditi',            key: 'redditi' },
    { to: '/dichiarazioni', icon: '📋', label: 'Dichiarativi', key: 'dichiarazioni' },
    { to: '/dichiarazioni', icon: '📋', label: 'Dichiarativi', key: 'dichiarazioni' },
    { to: '/dichiarazioni', icon: '📋', label: 'Dichiarativi', key: 'dichiarazioni' },
    { to: '/f24',       icon: '◑', label: 'F24 e Versamenti',   key: 'f24' },
    { to: '/avvisi',    icon: '◒', label: 'Avvisi e Cartelle',  key: 'avvisi' },
    { to: '/rateazioni',icon: '◗', label: 'Rateazioni',         key: 'rateazioni' },
    { section: 'Società' },
    { to: '/cedenti',   icon: '◓', label: 'Cedenti',            key: 'cedenti' },
    { to: '/cessquote', icon: '◔', label: 'Cessione Quote',     key: 'cessquote' },
    { section: 'Contabilità' },
    { to: '/banche',    icon: '◕', label: 'Banche',             key: 'banche' },
    { to: '/bilanci',   icon: '📊', label: 'Bilanci',            key: 'bilanci' },
    { to: '/attivita',  icon: '◖', label: 'Attività',           key: 'attivita' },
    { section: 'Strumenti' },
    { to: '/import-rateazione', icon: '📥', label: 'Import Rateazione', key: 'import' },
    { to: '/onedrive',  icon: '☁', label: 'Archivio Google Drive', key: 'onedrive' },
    { to: '/credenziali', icon: '🔑', label: 'Credenziali', key: 'credenziali' },
    { to: '/progetti',  icon: '📁', label: 'Progetti',           key: 'progetti' },
    ...(isAdmin ? [{ to: '/utenti', icon: '👥', label: 'Utenti', key: 'utenti' }] : []),
    { to: '/impostazioni', icon: '⚙', label: 'Impostazioni', key: 'impostazioni' },
  ];

  // Filtra per collaboratori
  const navItems = allItems.filter(item => {
    if (item.section) return true; // sezioni mostrate sempre (verranno filtrate sotto)
    return canSee(item.key || '');
  });

  // Rimuovi sezioni vuote
  const filteredNav = navItems.filter((item, i) => {
    if (!item.section) return true;
    // Controlla se la sezione ha almeno un item visibile dopo di essa
    for (let j = i+1; j < navItems.length; j++) {
      if (navItems[j].section) break;
      return true;
    }
    return false;
  });

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-title">Studio SP</div>
        <div className="sidebar-logo-sub">Gestionale</div>
      </div>
      <nav className="sidebar-nav">
        {filteredNav.map((item, i) =>
          item.section ? (
            <div key={i} className="nav-section">{item.section}</div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
            </NavLink>
          )
        )}
      </nav>
      <div className="sidebar-user" style={{ flexDirection:'column', gap:0, padding:'10px 12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, width:'100%' }}>
          <div className="user-avatar">{(currentUser?.nome||'SP').slice(0,2).toUpperCase()}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div className="user-name" style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {currentUser?.nome || 'Studio SP'}
            </div>
            <div className="user-role">{currentUser?.ruolo || 'Amministratore'}</div>
          </div>
        </div>
        {onLogout && (
          <button onClick={onLogout}
            style={{ marginTop:8, width:'100%', padding:'5px 0', fontSize:12, cursor:'pointer',
              background:'transparent', border:'1px solid var(--border-strong)', borderRadius:'var(--radius-sm)',
              color:'var(--text-muted)', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            ⎋ Logout
          </button>
        )}
      </div>
    </aside>
  );
}

// ─── TOPBAR ───────────────────────────────────────────────────────────────────
export function Topbar({ title, actions }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/clienti?q=${encodeURIComponent(q.trim())}`);
  };

  const today = new Date().toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const todayCap = today.charAt(0).toUpperCase() + today.slice(1);

  return (
    <div className="topbar">
      <span className="topbar-title">{title}</span>
      {actions && <div style={{ display:'flex', gap:8 }}>{actions}</div>}
      <form onSubmit={handleSearch} style={{ marginLeft:'auto', marginRight:0 }}>
        <div className="topbar-search">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input placeholder="Cerca cliente…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
      </form>
      <span className="topbar-date">{todayCap}</span>
    </div>
  );
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
export function ToastContainer({ toasts, removeToast }) {
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast ${t.type || ''}`} onClick={() => removeToast(t.id)}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = '') => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3000);
  }, []);
  const removeToast = useCallback((id) => setToasts(p => p.filter(t => t.id !== id)), []);
  return { toasts, addToast, removeToast };
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, footer, size = 'md' }) {
  const modalRef = React.useRef(null);
  const drag     = React.useRef({ on: false, sx: 0, sy: 0, il: 0, it: 0 });
  const resize   = React.useRef({ on: false, edge: '', sx: 0, sy: 0, iw: 0, ih: 0, il: 0, it: 0 });

  const initW = size === 'lg' ? 900 : size === 'sm' ? 440 : 680;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const onHeaderMouseDown = (e) => {
    if (e.target.closest('button')) return;
    const el = modalRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.position = 'fixed'; el.style.margin = '0';
    el.style.left = r.left + 'px'; el.style.top = r.top + 'px';
    drag.current = { on: true, sx: e.clientX, sy: e.clientY, il: r.left, it: r.top };
    e.preventDefault();
  };

  // ── Resize ────────────────────────────────────────────────────────────────
  const onResizeMouseDown = (e, edge) => {
    const el = modalRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.position = 'fixed'; el.style.margin = '0';
    el.style.left = r.left + 'px'; el.style.top = r.top + 'px';
    el.style.maxWidth = 'none';
    resize.current = { on: true, edge, sx: e.clientX, sy: e.clientY,
      iw: r.width, ih: r.height, il: r.left, it: r.top };
    e.preventDefault(); e.stopPropagation();
  };

  useEffect(() => {
    const move = (e) => {
      const el = modalRef.current; if (!el) return;
      // Drag
      if (drag.current.on) {
        el.style.left = (drag.current.il + e.clientX - drag.current.sx) + 'px';
        el.style.top  = (drag.current.it + e.clientY - drag.current.sy) + 'px';
      }
      // Resize
      if (resize.current.on) {
        const { edge, sx, sy, iw, ih, il, it } = resize.current;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (edge.includes('e'))  el.style.width  = Math.max(340, iw + dx) + 'px';
        if (edge.includes('s'))  el.style.height = Math.max(200, ih + dy) + 'px';
        if (edge.includes('w')) { el.style.width  = Math.max(340, iw - dx) + 'px'; el.style.left = (il + dx) + 'px'; }
        if (edge.includes('n')) { el.style.height = Math.max(200, ih - dy) + 'px'; el.style.top  = (it + dy) + 'px'; }
      }
    };
    const up = () => { drag.current.on = false; resize.current.on = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  const handle = (edge, style) => (
    <div onMouseDown={e => onResizeMouseDown(e, edge)} style={{
      position:'absolute', zIndex:10, ...style,
      background:'transparent',
    }} />
  );

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" ref={modalRef} style={{ maxWidth: initW, position:'relative', overflow:'visible' }}>
        {/* Resize handles */}
        {handle('e',  { right:-4, top:10, bottom:10, width:8, cursor:'ew-resize' })}
        {handle('w',  { left:-4,  top:10, bottom:10, width:8, cursor:'ew-resize' })}
        {handle('s',  { bottom:-4, left:10, right:10, height:8, cursor:'ns-resize' })}
        {handle('n',  { top:-4, left:10, right:10, height:8, cursor:'ns-resize' })}
        {handle('se', { right:-4, bottom:-4, width:12, height:12, cursor:'se-resize' })}
        {handle('sw', { left:-4,  bottom:-4, width:12, height:12, cursor:'sw-resize' })}
        {handle('ne', { right:-4, top:-4, width:12, height:12, cursor:'ne-resize' })}
        {handle('nw', { left:-4,  top:-4, width:12, height:12, cursor:'nw-resize' })}

        <div className="modal-header" onMouseDown={onHeaderMouseDown}
          title="Trascina per spostare · Bordi per ridimensionare"
          style={{ cursor:'move' }}>
          <span className="modal-title">{title}</span>
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ fontSize:10, color:'var(--text-faint)', marginRight:6 }}>↔ ridimensionabile</span>
            <button className="btn btn-icon" onClick={onClose} style={{ fontSize:18, lineHeight:1 }}>×</button>
          </div>
        </div>
        <div className="modal-body" style={{ overflowY:'auto', maxHeight:'calc(90vh - 120px)' }}>{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ─── CONFIRM DIALOG ────────────────────────────────────────────────────────────
export function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Conferma</span>
        </div>
        <div className="modal-body">
          <p style={{ fontSize:14 }}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCancel}>Annulla</button>
          <button className="btn btn-danger" onClick={onConfirm}>Elimina</button>
        </div>
      </div>
    </div>
  );
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
export function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

export function formatEuro(n) {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('it-IT', { style:'currency', currency:'EUR' }).format(n);
}

export function clienteNome(c) {
  if (!c) return '—';
  if (c.denominazione) return c.denominazione;
  if (c.ragione_sociale) return c.ragione_sociale;
  return [c.nome, c.cognome].filter(Boolean).join(' ') || '—';
}

export function scadenzaColor(data_scad, stato) {
  if (stato === 'COMPLETATA') return 'pill-green';
  if (stato === 'ANNULLATA')  return 'pill-gray';
  const d = new Date(data_scad);
  const now = new Date();
  const diff = (d - now) / 86400000;
  if (diff < 0)  return 'pill-red';
  if (diff <= 7) return 'pill-red';
  if (diff <= 30)return 'pill-amber';
  return 'pill-blue';
}

export function statoLabel(stato) {
  const map = {
    // Scadenze
    APERTA:         'Aperta',
    PREPARATA:      'Preparata',
    INVIATA:        'Inviata',
    SOSPESA:        'Sospesa',
    NON_PREVISTA:   'Non prevista',
    ALTRO:          'Altro',
    // Avvisi e Cartelle
    APERTO:         'Aperto',
    CHIUSO:         'Chiuso',
    IN_LAVORAZIONE: 'In lavorazione',
    SGRAVIO:        'Sgravio',
    SGRAVIO_PARZIALE: 'Sgravio parziale',
    RATEIZZATO:     'Rateizzato',
    PAGATO:         'Pagato',
    // legacy
    IN_CORSO:       'In corso',
    COMPLETATA:     'Completata',
    ANNULLATA:      'Annullata',
  };
  return map[stato] || stato;
}

export function statoPillClass(stato) {
  const map = {
    // Scadenze
    APERTA:         'pill-red',
    PREPARATA:      'pill-amber',
    INVIATA:        'pill-green',
    SOSPESA:        'pill-gray',
    NON_PREVISTA:   'pill-gray',
    ALTRO:          'pill-blue',
    // Avvisi
    APERTO:         'pill-red',
    CHIUSO:         'pill-gray',
    IN_LAVORAZIONE: 'pill-amber',
    SGRAVIO:        'pill-green',
    SGRAVIO_PARZIALE: 'pill-amber',
    RATEIZZATO:     'pill-blue',
    PAGATO:         'pill-green',
    // legacy
    IN_CORSO:       'pill-amber',
    COMPLETATA:     'pill-green',
    ANNULLATA:      'pill-gray',
  };
  return map[stato] || 'pill-gray';
}

export function LoadingSpinner() {
  return (
    <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-faint)' }}>
      <div style={{ fontSize:13 }}>Caricamento…</div>
    </div>
  );
}

export function Pagination({ page, total, limit, onPage }) {
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  return (
    <div className="pagination">
      <button className="page-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>‹</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
        <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page === pages} onClick={() => onPage(page + 1)}>›</button>
    </div>
  );
}
